import type { Theme } from "@mui/material"
import { Kinds, getLabColorByKind } from "@pharmupp/p3-adherent-domain"
import {
  type ChallengeFamilyName,
  type LaboratorySplit,
  MarketShareDistributions,
  MarketShareKinds,
  type MarketShareLaboratory,
  buildLaboratorySplits,
  challengeFamilyEnum,
  formatPrice,
  labelOf,
} from "@pharmupp/p3-front-commons"
import type { ApiCategoricalMarketShares } from "./categoricalMarketShares.model"

interface CategoricalMarketShare {
  name: ChallengeFamilyName
  label: string
  challengePercentage?: number
  laboratorySplits: LaboratorySplit[]
  tooltip?: string | null
}

interface CategoricalMarketShares {
  marketShares: CategoricalMarketShare[]
  syncDate?: string
}

const distribution = MarketShareDistributions.SALES

const salesOf = (lab: MarketShareLaboratory) => lab[distribution]?.mainStat ?? 0

const isFeatured = (lab: MarketShareLaboratory) =>
  lab.kind === MarketShareKinds.PRIORITY

const computeChallengePercentage = (
  laboratories: MarketShareLaboratory[],
  totalSales: number,
) => {
  if (totalSales === 0) return undefined
  const challengeSales = laboratories
    .filter(isFeatured)
    .reduce((sum, lab) => sum + salesOf(lab), 0)
  return (challengeSales * 100) / totalSales
}

export const selectCategoricalMarketShares =
  (palette: Theme["palette"]) =>
  ({
    categoricalChallengeFamilies: families = [],
  }: ApiCategoricalMarketShares): CategoricalMarketShares => ({
    marketShares: families.map((family) => {
      const totalSales = family[distribution]?.mainStat ?? 0
      const laboratories = family.laboratories ?? []

      return {
        name: family.name,
        label: labelOf(challengeFamilyEnum, family.name),
        challengePercentage: computeChallengePercentage(laboratories, totalSales),
        tooltip: family.tooltip,
        laboratorySplits: buildLaboratorySplits({
          laboratories,
          distribution,
          getLabColorByKind: (kind) =>
            getLabColorByKind(
              kind === MarketShareKinds.OTHER ? Kinds.OTHER : Kinds.CHALLENGE,
              palette,
            ),
          totalSales,
          formatValue: formatPrice,
        }),
      }
    }),
    syncDate: families
      .map((family) => family.periodEndDate)
      .filter((date): date is string => !!date)
      .sort()[0],
  })
